"use client";

import { displayLabel } from "@/lib/display";
import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { toast } from "sonner";

const ReactMarkdown = dynamic(() => import("react-markdown"), { ssr: false, loading: () => null });
import { Bot, Clipboard, Download, Pause, Play, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { dumpSession } from "@/lib/api";
import { type CallStatus } from "@/lib/ws";
import { AudioPlayerBar } from "@/components/audio-player-bar";
import { PilotCard } from "@/components/pilot-card";
import { TurnDebug } from "@/components/turn-debug";

export interface Message {
  role: "user" | "assistant";
  content: string;
  audioSrc?: string;
  debug?: Record<string, unknown>;
}

export interface HistoryEntry {
  role: string;
  content: string;
  debug?: Record<string, unknown>;
  audio_url?: string;
}

export function mapHistoryToMessages(
  history: HistoryEntry[],
  sessionId: string,
  hasAudio: boolean | undefined,
  audioUrlBuilder: (sessionId: string, turnIndex: number) => string,
): Message[] {
  return history
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) => {
      const turnIndex = m.audio_url ? parseInt(m.audio_url.split("/").pop()!, 10) : -1;
      const showTurnAudio = m.audio_url && !(hasAudio && turnIndex === 0);
      return {
        role: m.role as "user" | "assistant",
        content: m.content,
        debug: m.debug,
        audioSrc: showTurnAudio ? audioUrlBuilder(sessionId, turnIndex) : undefined,
      };
    });
}

interface ChatProps {
  messages: Message[];
  ended: boolean;
  loading: boolean;
  onSend: (text: string) => void;
  sessionId: string;
  callStatus?: CallStatus;
  hangupCause?: string;
  hangupBy?: string;
  onHangUp?: () => void;
  onNewCall?: () => void;
  recordingUrl?: string;
  userRecordingUrl?: string;
  aiRecordingUrl?: string;
  recordingReady?: boolean;
  children?: React.ReactNode;
  dumpFn?: (id: string) => Promise<Record<string, unknown>>;
  enableMarkdown?: boolean;
  readOnly?: boolean;
}

const STATUS_LABELS: Record<CallStatus, string> = {
  idle: "空闲",
  listening: "聆听中...",
  user_speaking: "用户讲话中...",
  processing: "处理中...",
  assistant_speaking: "助手讲话中...",
  dialing: "拨号中...",
  ringing: "振铃中...",
  answered: "对话中",
  ended: "对话已结束",
};

const STATUS_DOT_COLOR: Record<CallStatus, string> = {
  idle: "bg-muted-foreground",
  listening: "bg-green-500",
  user_speaking: "bg-green-500",
  processing: "bg-amber-500",
  assistant_speaking: "bg-green-500",
  dialing: "bg-amber-500",
  ringing: "bg-amber-500",
  answered: "bg-green-500",
  ended: "bg-red-500",
};

export function AudioButton({ src }: { src: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  function toggle() {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play();
    }
  }

  return (
    <>
      <audio
        ref={audioRef}
        src={src}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      <button
        onClick={toggle}
        className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20"
        aria-label={playing ? "暂停音频" : "播放音频"}
      >
        {playing ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
      </button>
      <a href={src} download className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20" aria-label="下载音频">
        <Download className="h-3 w-3" />
      </a>
    </>
  );
}


type TrackKey = "all" | "user" | "ai";

function RecordingToolbar({
  src,
  userSrc,
  aiSrc,
  disabled,
}: {
  src: string;
  userSrc?: string;
  aiSrc?: string;
  disabled?: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [active, setActive] = useState<TrackKey | null>(null);

  function togglePlay(track: TrackKey, url: string) {
    const audio = audioRef.current;
    if (!audio) return;
    if (active === track) {
      audio.pause();
      setActive(null);
      return;
    }
    audio.src = url;
    setActive(track);
    audio.play();
  }

  const tracks: { key: TrackKey; url: string; icon: React.ReactNode; label: string }[] = [
    { key: "all", url: src, icon: <Play className="h-4 w-4" />, label: "录音" },
    ...(userSrc ? [{ key: "user" as const, url: userSrc, icon: <User className="h-4 w-4" />, label: "用户音频" }] : []),
    ...(aiSrc ? [{ key: "ai" as const, url: aiSrc, icon: <Bot className="h-4 w-4" />, label: "AI 音频" }] : []),
  ];

  return (
    <>
      <audio
        ref={audioRef}
        preload="none"
        onEnded={() => setActive(null)}
      />
      {tracks.map(({ key, url, icon, label }) => {
        const isActive = active === key;
        return (
          <Button
            key={key}
            type="button"
            variant="outline"
            size="icon"
            aria-label={isActive ? `暂停${label}` : `播放${label}`}
            title={isActive ? `暂停${label}` : `播放${label}`}
            onClick={() => togglePlay(key, url)}
            disabled={disabled}
          >
            {isActive ? <Pause className="h-4 w-4" /> : icon}
          </Button>
        );
      })}
      {disabled ? (
        <Button type="button" variant="outline" size="icon" disabled aria-label="下载录音" title="录音尚未就绪">
          <Download className="h-4 w-4" />
        </Button>
      ) : (
        <Button type="button" variant="outline" size="icon" asChild aria-label="下载录音" title="下载录音">
          <a href={src} download>
            <Download className="h-4 w-4" />
          </a>
        </Button>
      )}
    </>
  );
}


export function Chat({ messages, ended, loading, onSend, sessionId, callStatus, hangupCause, hangupBy, onHangUp, onNewCall, recordingUrl, userRecordingUrl, aiRecordingUrl, recordingReady, children, dumpFn, enableMarkdown = false, readOnly }: ChatProps) {
  const effectiveDump = dumpFn ?? dumpSession;
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!loading && !ended) {
      inputRef.current?.focus();
    }
  }, [loading, ended]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput("");
    onSend(text);
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <ScrollArea className="min-h-0 flex-1 p-4">
        <div className="space-y-3">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : enableMarkdown
                      ? "bg-muted text-foreground prose prose-sm dark:prose-invert"
                      : "bg-muted text-foreground whitespace-pre-wrap"
                }`}
              >
                {msg.role === "assistant" && enableMarkdown
                  ? <ReactMarkdown>{msg.content}</ReactMarkdown>
                  : msg.content}
                {msg.audioSrc && <AudioButton src={msg.audioSrc} />}
                {msg.debug && <PilotCard debug={msg.debug} />}
                {msg.debug && <TurnDebug debug={msg.debug} />}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                ...
              </div>
            </div>
          )}
          {ended && !callStatus && (
            <div className="flex items-center gap-3 py-3 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-border" />
              <span>会话已结束</span>
              <div className="h-px flex-1 bg-border" />
            </div>
          )}
          {children}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      <div className="flex gap-2 border-t p-4">
        {readOnly && recordingUrl ? (
          <AudioPlayerBar
            src={recordingUrl}
            userSrc={userRecordingUrl}
            aiSrc={aiRecordingUrl}
            disabled={recordingReady === false}
          />
        ) : (
          <>
            {callStatus && onHangUp ? (
              <div className="flex flex-1 items-center gap-3">
                <span className="flex items-center gap-2 flex-1 text-sm text-muted-foreground">
                  <span className={`inline-block h-2 w-2 rounded-full ${STATUS_DOT_COLOR[ended ? "ended" : callStatus]}`} />
                  {STATUS_LABELS[ended ? "ended" : callStatus]}
                  {ended && hangupCause && (
                    <span className="ml-1 text-xs text-muted-foreground">({displayLabel(hangupCause)})</span>
                  )}
                  {ended && hangupBy && (
                    <span className="ml-1 text-xs text-muted-foreground">
                      {hangupBy === "nord" ? "AI 挂断" : "对方挂断"}
                    </span>
                  )}
                </span>
                {ended && onNewCall ? (
                  <Button onClick={onNewCall}>新建对话</Button>
                ) : (
                  <Button
                    variant="destructive"
                    onClick={onHangUp}
                    disabled={callStatus === "idle"}
                  >
                    挂断
                  </Button>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-1 gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={ended ? "会话已结束" : "输入消息..."}
                  disabled={ended || loading}
                />
                <Button type="submit" disabled={ended || loading || !input.trim()}>
                  发送
                </Button>
              </form>
            )}
            {recordingUrl && (
              <RecordingToolbar
                src={recordingUrl}
                userSrc={userRecordingUrl}
                aiSrc={aiRecordingUrl}
                disabled={recordingReady === false}
              />
            )}
          </>
        )}
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="复制会话调试数据"
          title="复制会话调试数据"
          onClick={async () => {
            try {
              const data = await effectiveDump(sessionId);
              await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
              toast.success("已复制");
            } catch (err) {
              toast.error(err instanceof Error ? err.message : String(err));
            }
          }}
        >
          <Clipboard className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="下载会话调试数据（JSON）"
          title="下载会话调试数据（JSON）"
          onClick={async () => {
            try {
              const data = await effectiveDump(sessionId);
              const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `session-${sessionId}.json`;
              a.click();
              URL.revokeObjectURL(url);
            } catch (err) {
              toast.error(err instanceof Error ? err.message : String(err));
            }
          }}
        >
          <Download className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
